import React, { useEffect, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { useDebounce } from "use-debounce";

const GenericAutoComplete = ({
  label,
  onSelect,
  fetchData,
  options,
  loading,
  required,
  getOptionLabel = (option) => option?.name || "",
  debounceTime = 500,
  defaultValue = null, // Valeur par défaut transmise par le parent
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultValue);
  const [error, setError] = useState(false);

  // Détection des modifications avec debounce
  const [debouncedSearchText] = useDebounce(searchText, debounceTime);

  // Fetch les données lorsque le texte debounced change
  useEffect(() => {
    if (debouncedSearchText) {
      fetchData(debouncedSearchText);
    }
  }, [debouncedSearchText, fetchData]);

  // Gérer la mise à jour de la valeur par défaut
  useEffect(() => {
    setSelectedOption(defaultValue); // Reset selectedOption to the provided defaultValue, even if null or undefined
    setSearchText(""); // Clear search text on reset
  }, [defaultValue]);

  // Validate selection on blur if required
  const handleBlur = () => {
    if (required && !selectedOption) {
      setError(true);
    } else {
      setError(false);
    }
  };

  return (
    <Autocomplete
      options={options}
      loading={loading}
      value={selectedOption} // Gère la sélection actuelle
      onChange={(event, newValue) => {
        setSelectedOption(newValue);
        onSelect(newValue);
        setError(false); // Clear error on valid selection
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          onChange={(event) => setSearchText(event.target.value)}
          onBlur={handleBlur} // Validate on blur
          required={required}
          error={error} // Show error state
          helperText={error ? `Veuillez sélectionner un ${label}` : ""} // Display helper text for error
        />
      )}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => (
        <li {...props} key={option._id || option.name}>
          {getOptionLabel(option)}
        </li>
      )}
    />
  );
};

export default GenericAutoComplete;
